import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { EmptyState, Input, PillButton, Card } from '../ui';
import { decryptFromShare, hashSharePassword } from '../services/shareCrypto';
import { ReactComponent as ShareIcon } from '../assets/icons/share.svg';
import { ReactComponent as LockIcon } from '../assets/icons/lock.svg';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import './PublicShare.css';

const FIELD_LABELS = { username: 'username', password: 'password', url: 'url', notes: 'notes', email: 'email' };

/**
 * Página pública de un enlace compartido (`/s/:id`, fuera del shell, sin
 * sesión). El backend nunca ve el ítem en claro: descifra en el navegador
 * con la clave K que viaja en el fragmento de la URL (después del `#`, que
 * el navegador nunca envía al servidor).
 */
function PublicShare() {
  const { id } = useParams();
  const { t } = useTranslation();

  const [state, setState] = useState('loading'); // loading | needsPassword | opened | error
  const [preview, setPreview] = useState(null);
  const [password, setPassword] = useState('');
  const [errorReason, setErrorReason] = useState(null);
  const [fields, setFields] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [opening, setOpening] = useState(false);

  const keyFragment = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';

  useEffect(() => {
    api.get(`/share/public/${id}`)
      .then((res) => {
        setPreview(res.data);
        setState(res.data.locked ? 'needsPassword' : 'ready');
      })
      .catch((err) => {
        setErrorReason(err.response?.data?.reason || 'not_found');
        setState('error');
      });
  }, [id]);

  const open = useCallback(async (pwd) => {
    setOpening(true);
    try {
      const body = pwd ? { passwordHash: await hashSharePassword(pwd) } : {};
      const res = await api.post(`/share/public/${id}/open`, body);
      const decrypted = await decryptFromShare({ ciphertext: res.data.ciphertext, iv: res.data.iv, keyBase64url: keyFragment });
      setFields(decrypted);
      setState('opened');
    } catch (err) {
      const reason = err.response?.data?.reason;
      if (reason === 'wrong_password') {
        setErrorReason('wrong_password');
        setState('needsPassword');
      } else {
        setErrorReason(reason || 'not_found');
        setState('error');
      }
    } finally {
      setOpening(false);
    }
  }, [id, keyFragment]);

  const copy = useCallback((key, value) => {
    navigator.clipboard.writeText(String(value));
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  }, []);

  const errorMessage = {
    not_found: t('publicShare.notFound'),
    expired: t('publicShare.expired'),
    exhausted: t('publicShare.exhausted')
  }[errorReason] || t('publicShare.notFound');

  return (
    <div className="sgc-public-share">
      <Card className="sgc-public-share__box">
        {state === 'loading' && <EmptyState icon={ShareIcon} title={t('common.loading')} />}

        {state === 'error' && (
          <EmptyState icon={AlertTriangleIcon} title={errorMessage} />
        )}

        {(state === 'ready' || state === 'needsPassword') && (
          <div className="sgc-public-share__form">
            <EmptyState icon={state === 'needsPassword' ? LockIcon : ShareIcon} title={preview?.itemName || t('shell.publicShareTitle')} />
            {state === 'needsPassword' && (
              <>
                <Input
                  icon={LockIcon}
                  type="password"
                  label={t('publicShare.passwordLabel')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={errorReason === 'wrong_password' ? t('publicShare.wrongPassword') : undefined}
                />
                <PillButton disabled={!password || opening} loading={opening} onClick={() => open(password)}>
                  {t('publicShare.unlock')}
                </PillButton>
              </>
            )}
            {state === 'ready' && (
              <PillButton disabled={opening} loading={opening} onClick={() => open()}>
                {t('publicShare.view')}
              </PillButton>
            )}
          </div>
        )}

        {state === 'opened' && fields && (
          <div className="sgc-public-share__fields">
            <p className="sgc-public-share__item-name">{preview?.itemName}</p>
            {Object.entries(fields).map(([key, value]) => (
              <Input
                key={key}
                readOnly
                label={t(`sharePage.field.${FIELD_LABELS[key] || key}`, key)}
                type={key === 'password' ? 'password' : 'text'}
                value={value}
                rightAction={(
                  <button type="button" onClick={() => copy(key, value)} title={t('otp.copy')}>
                    <CopyIcon width={16} height={16} aria-hidden="true" />
                  </button>
                )}
              />
            ))}
            {copiedKey && <p className="sgc-public-share__copied">{t('publicShare.copied')}</p>}
          </div>
        )}
      </Card>
    </div>
  );
}

export default PublicShare;
